/**
 * En tête qui est affiché en haut
 * Permettant de changer les options
 */

import React, { useEffect, useState } from "react";
import { Link } from "@reach/router";

import { usePrevLink, usePrevUrl, useReturnToSelfState, useUrl } from "@utils/navigation";
import { useRouteData } from "react-static";
import { WithPrevState } from "@utils/data";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCompressArrowsAlt, faCog, faVolumeUp, faVolumeMute } from "@fortawesome/free-solid-svg-icons";
import Logo from "@components/Logo";
import { useAppDispatch } from "@components/PersistedData/hooks";
import { toggleSound, useCurrentSettings } from "../PersistedData/settingsSlice";
import { useI18n } from "../../I18nextProvider";
// import { LocationType } from "../../utils/data";

const Header = () => {
  const i18n = useI18n();

  const dispatch = useAppDispatch();
  const settings = useCurrentSettings();

  const current = useRouteData();
  const { lang, type, otherLang, item } = current;

  const settingsUrl = useUrl({ type: "settings" });
  const homeURL = useUrl({ type: "home" });
  const settingState: WithPrevState = useReturnToSelfState();
  const isBrowser = typeof document !== "undefined";
  const [isFullscreen, setFullscreen] = useState<boolean>(isBrowser && !!document.fullscreenElement);
  const prevLink = usePrevUrl();

  const clickSettingsUrl = type === "settings" ? prevLink.path : settingsUrl;
  const clickSettingsState = type === "settings" ? prevLink.state : settingState;
  const disableSoundButton: boolean = !!(type === "cinematic" && item) || settings.mode === "noscript";

  const handleSoundClick = () => {
    dispatch(toggleSound());
    return false;
  };

  const handleFullscreenClick = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  };

  const onFullScreenChange = (ev: Event) => {
    setFullscreen(!!document.fullscreenElement);
  };

  const dummyChangeHandler = () => {
    // Supprime le warning à propos du input sans événement onChange
  };

  useEffect(() => {
    if (isBrowser) {
      document.addEventListener("fullscreenchange", onFullScreenChange);
      return () => {
        document.removeEventListener("fullscreenchange", onFullScreenChange);
      };
    }
  }, []);

  const fullScreenSupported = settings.mode !== "noscript" && document?.documentElement?.requestFullscreen;

  return (
    <header>
      <a className="skip-main" href="#maincontent" tabIndex={0}>
        {i18n.t("global.skipToContent")}
      </a>
      <div className="nom">
        <Link className="parametres" to={homeURL} tabIndex={0}>
          <p>{i18n.t("global.siteName")}</p>
        </Link>
      </div>

      <div className="menu">
        {fullScreenSupported && (
          <div className="fullscreen">
            <label htmlFor="fullscreen" aria-labelledby="label-fullscreen">
              {" "}
              <input
                tabIndex={0}
                type="checkbox"
                id="fullscreen"
                checked={isFullscreen}
                disabled={!fullScreenSupported}
                onClick={handleFullscreenClick}
                onChange={dummyChangeHandler}
                aria-labelledby="label-fullscreen"
              />
              <span id="label-fullscreen" hidden>
                {i18n.t("global.fullscreen")}
              </span>
              <span className="cocher">
                <FontAwesomeIcon icon={faCompressArrowsAlt} />
              </span>
            </label>
          </div>
        )}

        <div className="volume">
          <label htmlFor="sound" aria-labelledby="label-sound">
            <input
              tabIndex={0}
              type="checkbox"
              id="sound"
              checked={settings.soundEnabled}
              disabled={disableSoundButton}
              onClick={handleSoundClick}
              onChange={dummyChangeHandler}
              aria-labelledby="label-sound"
            />
            <span id="label-sound" hidden>
              {i18n.t("global.sound")}
            </span>
            <span className="cocher">
              <FontAwesomeIcon icon={settings.soundEnabled ? faVolumeUp : faVolumeMute} />
            </span>
          </label>
        </div>
        <Link
          tabIndex={0}
          className="parametres"
          to={clickSettingsUrl}
          state={clickSettingsState}
          aria-labelledby="label-parameters"
        >
          <FontAwesomeIcon icon={faCog} />
          <span id="label-parameters" hidden>
            {i18n.t("settings.title")}
          </span>
        </Link>
        <a tabIndex={0} className="langue" href={otherLang} aria-labelledby="label-langswitch">
          {lang === "fr" ? "EN" : "FR"}
          <span id="label-langswitch" hidden>
            {i18n.t("global.langSwitch")}
          </span>
        </a>
      </div>
      <Logo />
    </header>
  );
};

export default Header;
